import { Box, Grid } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import RemoveNullObjectValue, { removeFalseValues } from 'src/utils/RemoveNullObjectValue';
import { useTranslation } from 'react-i18next';
import agentRole from 'src/pages/agentRole/selector';
import RHFTextField from '../../../components/hook-form/RHFTextField';
import FormProvider from '../../../components/hook-form/FormProvider';
import validation from './validation';
import Enum from '../enum';
import { useMutationCustom, useQueryCustom } from '../../../utils/reactQueryHooks';
import axiosInstance from '../../../utils/axios';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import WaitingBox from '../../../components/WaitingBox/index';
import persianToEnglishNumber from '../../../utils/persianToEnglishNumber';
import AddPermissions from './Permissions';
import ProvinceSelector from 'src/components/selector/Province';
import { useDispatch, useSelector } from 'react-redux';
import { getAdminProfile } from 'src/redux/slices/user';
import RHFSelector from 'src/components/hook-form/RHFSelector';
import { CLIENT_TYPE_OP } from 'src/config';
import { RHFCheckbox } from 'src/components/hook-form';

const Add = ({ onClose }) => {
  const admin = useSelector((store) => store.admin.data);
  const dispatch = useDispatch();

  const { t } = useTranslation();
  const queryParams = useParams();
  const paramId = queryParams?.id;

  const navigate = useNavigate();

  const backUrl = `${Enum.routes.root}`;
  const methods = useForm({
    resolver: yupResolver(validation.schema(paramId)),
    defaultValues : validation.defaultValue
  });

  const {
    reset,
    watch,
    control,
    setValue,
    setError,
    handleSubmit,
    getValues,
    formState: { errors, isSubmitting },
  } = methods;

  // ----------------------------------------------------------------------------- SERVICE
  const creating = (params) => axiosInstance.post(Enum?.api?.base, params);
  const updating = (params) => axiosInstance.put(`${Enum?.api?.base}/${paramId}`, params);
  const getById = () => axiosInstance.get(`${Enum?.api?.base}/${paramId}`);
  // ------------------------------------------------------------------------------ Mutation
  const onSuccessMutating = () => {
    toast.success(t('successfully'));
    const values = getValues();
    if(values[validation.fieldNames.email] === admin?.email){
      dispatch(getAdminProfile())
    }
    navigate(backUrl);
  };
  // const onErrorMutating = (error) => {
  //   console.log('* * * onErrorMutating :', { error });
  //   const errorTitle = error.response.data.message || t('errorTryAgain');
  //   const errors = Object.values(error?.response?.data?.errors || {});
  //   if (errors?.length) {
  //     errors?.map((x) => {
  //       return toast.error(x?.[0]);
  //     });
  //   } else toast.error(errorTitle);
  // };
  const { isLoading, mutate } = useMutationCustom({
    url: paramId ? updating : creating,
    name: `${Enum?.api?.base}_update`,
    invalidQuery: `${Enum?.api?.base}_get`,
    onSuccess: onSuccessMutating,
    // onError: onErrorMutating,
  });

  // ---------------------------------------
  const onSuccess = (resData) => {
    console.log('* * * onSuccess', { resData });
    const resetData = {
      [validation.fieldNames.name]: resData?.data?.[validation.fieldNames.name] || '',
      [validation.fieldNames.last_name]: resData?.data?.[validation.fieldNames.last_name] || '',
      [validation.fieldNames.code_melli]: resData?.data?.[validation.fieldNames.code_melli] || '',
      [validation.fieldNames.email]: resData?.data?.[validation.fieldNames.email] || '',
      [validation.fieldNames.password]: resData?.data?.[validation.fieldNames.password],
      [validation.fieldNames.shenasname_number]: resData?.data?.[validation.fieldNames.shenasname_number] || '',
      [validation.fieldNames.agent_id]: agentRole.convertor.object(resData?.data?.agent),
      [validation.fieldNames.province_id]: ProvinceSelector.convertor.array(resData?.data?.province),
      [validation.fieldNames.client_type]: resData?.data?.client_type ? {label : resData?.data?.client_type , value : resData?.data?.client_type} : null,
      [validation.fieldNames.phone]: resData?.data?.[validation.fieldNames.phone] || '',
      [validation.fieldNames.can_login]: resData?.data?.[validation.fieldNames.can_login] || false,
      [validation.fieldNames.permissions]: {},
    };

    resData.data?.role?.permission?.forEach((x) => {
      resetData[validation.fieldNames.permissions][x.id] = true;
    });
    console.log('* * * onSuccess', { resetData });
    reset(resetData);
  };
  const dataById = useQueryCustom({
    name: `getById_${Enum?.api?.base}_${paramId}`,
    url: getById,
    onSuccess: onSuccess,
    enabled: !!paramId,
  });

  const watchStartDate = watch(validation.fieldNames.startDate);
  console.log({ watchStartDate });
  const onSubmit = async () => {
    const values = getValues();
    console.log('* * * onSubmit : ', { values });

    const reqData = {
      [validation.fieldNames.name]: values[validation.fieldNames.name]?.toString()?.trim() !== "" ?values[validation.fieldNames.name] : undefined,
      [validation.fieldNames.last_name]: values[validation.fieldNames.last_name]?.toString()?.trim() !== "" ? values[validation.fieldNames.last_name] :  undefined,
      [validation.fieldNames.code_melli]: values[validation.fieldNames.code_melli]?.toString()?.trim() !== "" ? persianToEnglishNumber(values[validation.fieldNames.code_melli]) : undefined,
      [validation.fieldNames.email]: values[validation.fieldNames.email],
      [validation.fieldNames.can_login]: values[validation.fieldNames.can_login] ? 1 : 0,
      [validation.fieldNames.password]: persianToEnglishNumber(values[validation.fieldNames.password]),
      // [validation.fieldNames.shenasname_number]: persianToEnglishNumber(
      //   values[validation.fieldNames.shenasname_number]
      // ),
      [validation.fieldNames.agent_id]: values[validation.fieldNames.agent_id]?.value,
      [validation.fieldNames.province_id]: values[validation.fieldNames.province_id]?.map(item => item?.value),
      [validation.fieldNames.client_type]: values[validation.fieldNames.client_type]?.value,
      [validation.fieldNames.phone]:values[validation.fieldNames.phone]?.toString()?.trim() !== "" ? persianToEnglishNumber(values[validation.fieldNames.phone]) : undefined,
      [validation.fieldNames.permissions]: Object.keys(
        RemoveNullObjectValue(removeFalseValues(values[validation.fieldNames.permissions]))
      ),
    };

    if(paramId){
      if(values[validation.fieldNames.password]?.toString()?.trim() === ''){
        delete reqData[validation.fieldNames.password]
      }
    }

    // const reqData = RemoveNullObjectValue(values);
    // Object.keys(reqData || {})?.map((x) => {
    //   formData.append(`permissions[]`, x);
    // });
    reqData[validation.fieldNames.permissions] = reqData[validation.fieldNames.permissions]?.map((x) => +x);

    console.log('* * * onSubmit : ', { reqData, values });
    mutate(reqData);
  };
  console.log({ queryParams });
  return dataById.isLoading ? (
    <WaitingBox />
  ) : (
    <Box
      sx={{
        width: '100%',
        // display: { xs: 'block', md: 'flex' },
        gap: 2,
        bgcolor: 'background.paper',
        borderRadius: 1,
        p: 4,
      }}
    >
      <HeaderBreadcrumbs
        back={`${Enum.routes.root}`}
        heading={
          paramId ? t('editAuthor', { author: Enum.title.name[0] }) : t('addAuthor', { author: Enum.title.name[0] })
        }
      >
        {/* <Typography sx={{ mt: 1 }}>{resQuery?.data?.data?.name}</Typography> */}
      </HeaderBreadcrumbs>
      {/* <Box
        sx={{
          textAlign: 'center',
          mb: '30px',
        }}
        onClick={() => navigate(backUrl)}
      >
        <Typography variant="h5">{'شما در حال ایجاد یک دوره جدید هستید!'}</Typography>
      </Box> */}

      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <RHFTextField name={validation.fieldNames.name} label={'نام'} />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFTextField name={validation.fieldNames.last_name} label={'نام خانوادگی'} />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFTextField name={validation.fieldNames.code_melli} label={'کدملی'} />
            </Grid>
            {/* <Grid item xs={12} md={6}>
              <RHFTextField name={validation.fieldNames.shenasname_number} label={'شماره شناسنامه'} />
            </Grid> */}
            <Grid item xs={12} md={6}>
              <ProvinceSelector.Element name={validation.fieldNames.province_id} label={'استان'} 
                disableClearable={false}
                multiple
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFTextField name={validation.fieldNames.phone} label={'موبایل'} />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFTextField name={validation.fieldNames.email} label={'ایمیل'} />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFTextField
                name={validation.fieldNames.password}
                label={'رمز عبور'}
                type={'password'}
                autoComplete={"new-password"}
                text={
                  paramId
                    ? 'در صورت نیاز به تغییر رمز عبور مقدار جدید را وارد کنید. در غیر این صورت آن را خالی بگزارید.'
                    : ''
                }
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFTextField name={validation.fieldNames.confirm_password} label={'تکرار رمز عبور'} type={'password'} />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFSelector
                options={CLIENT_TYPE_OP}
                name={validation.fieldNames.client_type}
                label={"نوع زائر"}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <agentRole.Element name={validation.fieldNames.agent_id} label={'سمت'} 
                hasIdFilter idsArr={Enum?.not_agent_id || []}
                  // queryParams={{
                  //   is_agent : 0
                  // }}
                  removeThisIds
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <RHFCheckbox 
                name={validation.fieldNames.can_login}
                label="دسترسی به سامانه"
              />
            </Grid>
          </Grid>
          <Grid item xs={12} textAlign="center">
            <LoadingButton type="submit" variant="contained" color={'success'} loading={isLoading}>
              {'ثبت اطلاعات'}
            </LoadingButton>
          </Grid>
        </Box>

        <Box
          sx={{
            my: 5,
          }}
        >
          {'دسترسی ها'}
        </Box>
        {/* role={false} */}

        {<AddPermissions />}

        <Box
          sx={{
            display: 'flex',
            mt: 3,
            gap: 3,
            justifyContent: 'center',
          }}
        >
          {/* <LoadingButton
            fullWidth
            // type="click"
            variant="outlined"
            color="success"
            loading={isLoading}
            onClick={onClose}
          >
            {'لغو'}
          </LoadingButton> */}

          <LoadingButton type="submit" variant="contained" color={'success'} loading={isLoading}>
            {'ثبت اطلاعات'}
          </LoadingButton>
        </Box>
      </FormProvider>
    </Box>
  );
};

export default Add;
